/**
 * Export Route Plan Service
 * Exporta planos de rota para Excel e PDF
 */

import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
// Utilidades de formatacao definidas localmente

// =======================================
// Utilidades
// =======================================

const safeText = (value) => {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) return value.toLocaleString();
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const isPngDataUrl = (value) => typeof value === 'string' && value.startsWith('data:image/png');

const extractBase64 = (dataUrl) => {
  if (!isPngDataUrl(dataUrl)) return null;
  const [, base64] = dataUrl.split(',');
  return base64 || null;
};

const hexToRgb = (hex) => {
  const clean = (hex || '#00d4aa').replace('#', '');
  const bigint = parseInt(clean, 16);
  return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
};

const formatTime = (seconds) => {
  if (!seconds) return '-';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return hours > 0 ? `${hours}h ${minutes}min` : `${minutes}min`;
};

const formatKm = (meters) => {
  if (!meters) return '-';
  return `${(meters / 1000).toFixed(1)} km`;
};

const getStopTypeName = (type) => {
  switch (type) {
    case 'start': return 'Partida';
    case 'end': return 'Chegada';
    case 'job': return 'Entrega';
    case 'pickup': return 'Coleta';
    case 'break': return 'Pausa';
    default: return type || 'Parada';
  }
};

const getStatusName = (status) => {
  switch (status) {
    case 'pending': return 'Pendente';
    case 'in_progress': return 'Em Andamento';
    case 'completed': return 'Concluido';
    case 'failed': return 'Falhou';
    case 'skipped': return 'Ignorado';
    default: return status || 'Pendente';
  }
};

const getPriorityName = (priority) => {
  switch (priority) {
    case 'urgent': return 'Urgente';
    case 'high': return 'Alta';
    case 'normal': return 'Normal';
    case 'low': return 'Baixa';
    default: return priority || 'Normal';
  }
};

// =======================================
// Exportacao Excel
// =======================================

/**
 * Exporta plano de rota para Excel
 * @param {Object} params - Parametros de exportacao
 * @param {string} params.planName - Nome do plano
 * @param {Array} params.routes - Array de rotas otimizadas
 * @param {Array} params.vehicles - Array de veiculos
 * @param {Array} params.deliveryPoints - Array de pontos de entrega
 * @param {Object} params.summary - Resumo da otimizacao
 * @param {Object} params.branding - Configuracoes de branding
 * @param {Object} params.theme - Tema do Material-UI
 */
export const exportRoutePlanExcel = async ({
  planName = 'Plano de Rota',
  routes = [],
  vehicles = [],
  deliveryPoints = [],
  summary = {},
  branding = {},
  theme,
}) => {
  const workbook = new ExcelJS.Workbook();
  const primaryColor = (theme?.palette?.primary?.main || '#00d4aa').replace('#', '').toUpperCase();
  const timestamp = new Date().toLocaleString('pt-BR');

  // Adicionar logo se disponivel
  let imageId = null;
  const base64Logo = extractBase64(branding.header?.logoUrl);
  if (base64Logo) {
    try {
      imageId = workbook.addImage({ base64: branding.header.logoUrl, extension: 'png' });
    } catch (err) {
      console.warn('exportRoutePlanExcel: nao foi possivel inserir logomarca', err);
    }
  }

  // =====================
  // Aba: Resumo
  // =====================
  const summarySheet = workbook.addWorksheet('Resumo');

  // Header
  let row = 1;
  const summaryTitle = summarySheet.addRow([branding.header?.title || planName]);
  summarySheet.mergeCells(row, 1, row, 4);
  summaryTitle.font = { size: 18, bold: true };
  summaryTitle.alignment = { horizontal: 'center' };
  row++;

  if (branding.header?.subtitle) {
    const subtitleRow = summarySheet.addRow([branding.header.subtitle]);
    summarySheet.mergeCells(row, 1, row, 4);
    subtitleRow.font = { size: 12 };
    subtitleRow.alignment = { horizontal: 'center' };
    row++;
  }

  const dateRow = summarySheet.addRow([`Gerado em: ${timestamp}`]);
  summarySheet.mergeCells(row, 1, row, 4);
  dateRow.font = { size: 10, italic: true };
  dateRow.alignment = { horizontal: 'center' };
  row += 2;

  // Metricas do resumo
  const summaryData = [
    ['Metrica', 'Valor'],
    ['Total de Rotas', routes.length],
    ['Total de Veiculos', vehicles.length],
    ['Total de Entregas', deliveryPoints.length],
    ['Distancia Total', formatKm(summary.totalDistance || routes.reduce((acc, r) => acc + (r.distance || 0), 0))],
    ['Tempo Total Estimado', formatTime(summary.totalDuration || routes.reduce((acc, r) => acc + (r.duration || 0), 0))],
    ['Entregas Atendidas', summary.deliveredCount || routes.reduce((acc, r) => acc + (r.steps?.filter(s => s.type === 'job').length || 0), 0)],
    ['Entregas Nao Atendidas', summary.unassignedCount || 0],
  ];

  summaryData.forEach((data, idx) => {
    const dataRow = summarySheet.addRow(data);
    if (idx === 0) {
      dataRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${primaryColor}` } };
        cell.alignment = { horizontal: 'center' };
      });
    }
  });

  summarySheet.getColumn(1).width = 25;
  summarySheet.getColumn(2).width = 20;

  // =====================
  // Aba: Rotas por Veiculo
  // =====================
  routes.forEach((route, routeIndex) => {
    const vehicle = vehicles.find(v => v.id === route.vehicleId) || {};
    const sheetName = `Rota ${routeIndex + 1} - ${(vehicle.name || `Veiculo ${route.vehicleId}`).substring(0, 20)}`;
    const routeSheet = workbook.addWorksheet(sheetName);

    // Header da rota
    let routeRow = 1;
    const routeTitleRow = routeSheet.addRow([`Rota: ${vehicle.name || `Veiculo ${route.vehicleId}`}`]);
    routeSheet.mergeCells(routeRow, 1, routeRow, 8);
    routeTitleRow.font = { size: 14, bold: true };
    routeRow++;

    const routeInfoRow = routeSheet.addRow([
      `Distancia: ${formatKm(route.distance)} | Tempo: ${formatTime(route.duration)} | Paradas: ${route.steps?.filter(s => s.type === 'job').length || 0}`,
    ]);
    routeSheet.mergeCells(routeRow, 1, routeRow, 8);
    routeInfoRow.font = { size: 11 };
    routeRow += 2;

    // Header da tabela de paradas
    const headers = ['#', 'Tipo', 'Local', 'Endereco', 'Chegada Est.', 'Tempo Servico', 'Prioridade', 'Status'];
    const headerRow = routeSheet.addRow(headers);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${primaryColor}` } };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
    });

    // Dados das paradas
    const steps = route.steps || [];
    steps.forEach((step, stepIndex) => {
      const arrivalTime = step.arrival
        ? new Date(step.arrival * 1000).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
        : '-';

      const stepRow = routeSheet.addRow([
        stepIndex + 1,
        getStopTypeName(step.type),
        step.name || step.description || '-',
        step.address || (step.location ? `${step.location[1]?.toFixed(6)}, ${step.location[0]?.toFixed(6)}` : '-'),
        arrivalTime,
        formatTime(step.service || step.serviceTime || 0),
        getPriorityName(step.priority),
        getStatusName(step.status),
      ]);

      stepRow.eachCell((cell) => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };
        cell.alignment = { vertical: 'middle' };
      });
    });

    // Ajustar largura das colunas
    routeSheet.getColumn(1).width = 5;
    routeSheet.getColumn(2).width = 12;
    routeSheet.getColumn(3).width = 25;
    routeSheet.getColumn(4).width = 35;
    routeSheet.getColumn(5).width = 12;
    routeSheet.getColumn(6).width = 15;
    routeSheet.getColumn(7).width = 12;
    routeSheet.getColumn(8).width = 12;
  });

  // =====================
  // Aba: Pontos de Entrega
  // =====================
  if (deliveryPoints.length > 0) {
    const deliverySheet = workbook.addWorksheet('Pontos de Entrega');

    let delRow = 1;
    const delTitleRow = deliverySheet.addRow(['Lista de Pontos de Entrega']);
    deliverySheet.mergeCells(delRow, 1, delRow, 7);
    delTitleRow.font = { size: 14, bold: true };
    delRow += 2;

    const delHeaders = ['#', 'Nome', 'Endereco', 'Latitude', 'Longitude', 'Prioridade', 'Tempo Servico'];
    const delHeaderRow = deliverySheet.addRow(delHeaders);
    delHeaderRow.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${primaryColor}` } };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
    });

    deliveryPoints.forEach((point, idx) => {
      const pointRow = deliverySheet.addRow([
        idx + 1,
        point.name || point.description || '-',
        point.address || '-',
        point.latitude?.toFixed(6) || point.location?.[1]?.toFixed(6) || '-',
        point.longitude?.toFixed(6) || point.location?.[0]?.toFixed(6) || '-',
        getPriorityName(point.priority),
        formatTime(point.serviceTime || point.service || 300),
      ]);
      pointRow.eachCell((cell) => {
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
      });
    });

    deliverySheet.getColumn(1).width = 5;
    deliverySheet.getColumn(2).width = 25;
    deliverySheet.getColumn(3).width = 40;
    deliverySheet.getColumn(4).width = 12;
    deliverySheet.getColumn(5).width = 12;
    deliverySheet.getColumn(6).width = 12;
    deliverySheet.getColumn(7).width = 15;
  }

  // Adicionar logo na primeira aba
  if (imageId !== null) {
    try {
      summarySheet.addImage(imageId, {
        tl: { col: 0, row: 0 },
        ext: { width: 150, height: 45 },
      });
    } catch (error) {
      console.warn('exportRoutePlanExcel: falha ao ancorar logomarca', error);
    }
  }

  // Salvar arquivo
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const filename = `${planName.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
  saveAs(blob, filename);
};

// =======================================
// Exportacao PDF
// =======================================

/**
 * Exporta plano de rota para PDF
 * @param {Object} params - Parametros de exportacao
 */
export const exportRoutePlanPdf = async ({
  planName = 'Plano de Rota',
  routes = [],
  vehicles = [],
  summary = {},
  branding = {},
  theme,
  t,
}) => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const primaryColor = theme?.palette?.primary?.main || '#00d4aa';
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const timestamp = new Date().toLocaleString('pt-BR');

  const header = branding.header || {};
  const footer = branding.footer ? safeText(branding.footer) : '';
  const logoUrl = header.logoUrl;

  let cursorY = 50;

  // Logo
  if (logoUrl && isPngDataUrl(logoUrl)) {
    try {
      const maxLogoWidth = Math.min(pageWidth * 0.25, 150);
      const logoHeight = maxLogoWidth * 0.3;
      doc.addImage(logoUrl, 'PNG', (pageWidth - maxLogoWidth) / 2, 20, maxLogoWidth, logoHeight);
      cursorY = 20 + logoHeight + 15;
    } catch (error) {
      console.warn('exportRoutePlanPdf: falha ao desenhar logomarca', error);
    }
  }

  // Titulo
  const centerX = pageWidth / 2;
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text(safeText(header.title || planName), centerX, cursorY, { align: 'center' });
  cursorY += 20;

  // Subtitulo
  if (header.subtitle) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.text(safeText(header.subtitle), centerX, cursorY, { align: 'center' });
    cursorY += 16;
  }

  // Data
  doc.setFontSize(10);
  doc.text(`Gerado em: ${timestamp}`, centerX, cursorY, { align: 'center' });
  cursorY += 25;

  // Watermark
  if (branding.watermark?.image && isPngDataUrl(branding.watermark.image)) {
    if (doc.saveGraphicsState && doc.restoreGraphicsState && doc.GState) {
      try {
        const wmWidth = pageWidth * 0.35;
        const wmHeight = wmWidth * 0.5;
        doc.saveGraphicsState();
        doc.setGState(new doc.GState({ opacity: branding.watermark.opacity ?? 0.05 }));
        doc.addImage(branding.watermark.image, 'PNG', (pageWidth - wmWidth) / 2, (pageHeight - wmHeight) / 2, wmWidth, wmHeight);
        doc.restoreGraphicsState();
      } catch (error) {
        console.warn('exportRoutePlanPdf: falha ao aplicar watermark', error);
      }
    }
  }

  // Tabela de resumo
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('Resumo da Otimizacao', 40, cursorY);
  cursorY += 10;

  const totalDistance = summary.totalDistance || routes.reduce((acc, r) => acc + (r.distance || 0), 0);
  const totalDuration = summary.totalDuration || routes.reduce((acc, r) => acc + (r.duration || 0), 0);
  const totalStops = routes.reduce((acc, r) => acc + (r.steps?.filter(s => s.type === 'job').length || 0), 0);

  const summaryTableData = [
    ['Total de Rotas', String(routes.length)],
    ['Total de Veiculos', String(vehicles.length)],
    ['Total de Entregas', String(totalStops)],
    ['Distancia Total', formatKm(totalDistance)],
    ['Tempo Total Estimado', formatTime(totalDuration)],
  ];

  doc.autoTable({
    startY: cursorY,
    head: [['Metrica', 'Valor']],
    body: summaryTableData,
    theme: 'grid',
    styles: { fontSize: 10, cellPadding: 6 },
    headStyles: { fillColor: hexToRgb(primaryColor), textColor: [255, 255, 255], fontStyle: 'bold' },
    columnStyles: { 0: { cellWidth: 180 }, 1: { cellWidth: 120 } },
    margin: { left: 40 },
  });

  cursorY = doc.lastAutoTable.finalY + 25;

  // Tabela de rotas
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('Detalhamento das Rotas', 40, cursorY);
  cursorY += 10;

  const routesTableData = routes.map((route, idx) => {
    const vehicle = vehicles.find(v => v.id === route.vehicleId) || {};
    const jobSteps = route.steps?.filter(s => s.type === 'job') || [];
    return [
      String(idx + 1),
      vehicle.name || `Veiculo ${route.vehicleId}`,
      String(jobSteps.length),
      formatKm(route.distance),
      formatTime(route.duration),
    ];
  });

  doc.autoTable({
    startY: cursorY,
    head: [['#', 'Veiculo', 'Paradas', 'Distancia', 'Tempo']],
    body: routesTableData,
    theme: 'grid',
    styles: { fontSize: 10, cellPadding: 6 },
    headStyles: { fillColor: hexToRgb(primaryColor), textColor: [255, 255, 255], fontStyle: 'bold' },
    margin: { left: 40 },
    didDrawPage: () => {
      // Footer em todas as paginas
      doc.setFontSize(9);
      doc.setTextColor(100);
      if (footer) {
        doc.text(footer, 40, pageHeight - 25);
      }
      const pageNumber = doc.internal.getNumberOfPages();
      const pageLabel = t ? t('reportPageLabel', { page: pageNumber }) : `Pagina ${pageNumber}`;
      doc.text(pageLabel, pageWidth - 100, pageHeight - 25);
    },
  });

  // Detalhes de cada rota em nova pagina
  routes.forEach((route, routeIndex) => {
    doc.addPage();
    const vehicle = vehicles.find(v => v.id === route.vehicleId) || {};

    let y = 50;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text(`Rota ${routeIndex + 1}: ${vehicle.name || `Veiculo ${route.vehicleId}`}`, 40, y);
    y += 18;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.text(`Distancia: ${formatKm(route.distance)} | Tempo: ${formatTime(route.duration)}`, 40, y);
    y += 25;

    // Tabela de paradas
    const stepsData = (route.steps || []).map((step, idx) => {
      const arrivalTime = step.arrival
        ? new Date(step.arrival * 1000).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
        : '-';
      return [
        String(idx + 1),
        getStopTypeName(step.type),
        (step.name || step.description || '-').substring(0, 30),
        (step.address || '-').substring(0, 40),
        arrivalTime,
        formatTime(step.service || step.serviceTime || 0),
        getStatusName(step.status),
      ];
    });

    doc.autoTable({
      startY: y,
      head: [['#', 'Tipo', 'Local', 'Endereco', 'Chegada', 'Servico', 'Status']],
      body: stepsData,
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 4 },
      headStyles: { fillColor: hexToRgb(primaryColor), textColor: [255, 255, 255], fontStyle: 'bold' },
      margin: { left: 40 },
      columnStyles: {
        0: { cellWidth: 25 },
        1: { cellWidth: 60 },
        2: { cellWidth: 120 },
        3: { cellWidth: 180 },
        4: { cellWidth: 55 },
        5: { cellWidth: 55 },
        6: { cellWidth: 60 },
      },
      didDrawPage: () => {
        doc.setFontSize(9);
        doc.setTextColor(100);
        if (footer) {
          doc.text(footer, 40, pageHeight - 25);
        }
        const pageNumber = doc.internal.getNumberOfPages();
        const pageLabel = t ? t('reportPageLabel', { page: pageNumber }) : `Pagina ${pageNumber}`;
        doc.text(pageLabel, pageWidth - 100, pageHeight - 25);
      },
    });
  });

  // Salvar PDF
  const filename = `${planName.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(filename);
};

// =======================================
// Exportacao GPX
// =======================================

/**
 * Gera cabecalho XML do GPX
 * @returns {string}
 */
const gpxHeader = (name) => `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="Fleet Premium - Traccar"
  xmlns="http://www.topografix.com/GPX/1/1"
  xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
  xsi:schemaLocation="http://www.topografix.com/GPX/1/1 http://www.topografix.com/GPX/1/1/gpx.xsd">
  <metadata>
    <name>${escapeXml(name)}</name>
    <time>${new Date().toISOString()}</time>
    <author>
      <name>Fleet Premium</name>
    </author>
  </metadata>`;

const gpxFooter = '</gpx>';

/**
 * Escapa caracteres especiais para XML
 */
const escapeXml = (text) => {
  if (!text) return '';
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
};

/**
 * Cria um waypoint GPX
 * @param {Object} point - Ponto de entrega
 * @param {number} index - Indice da parada
 * @returns {string}
 */
const createWaypoint = (point, index) => {
  const lat = point.latitude || point.lat || point.location?.[1];
  const lon = point.longitude || point.lng || point.location?.[0];

  if (!lat || !lon) return '';

  const name = point.name || point.description || `Parada ${index + 1}`;
  const desc = [
    point.address,
    point.notes,
    point.priority ? `Prioridade: ${getPriorityName(point.priority)}` : null,
    point.serviceTime ? `Tempo servico: ${formatTime(point.serviceTime)}` : null,
  ].filter(Boolean).join('\n');

  const timeWindow = point.timeWindowStart || point.timeWindowEnd
    ? `Janela: ${point.timeWindowStart || ''} - ${point.timeWindowEnd || ''}`
    : '';

  return `
  <wpt lat="${lat}" lon="${lon}">
    <name>${escapeXml(name)}</name>
    ${desc ? `<desc>${escapeXml(desc)}</desc>` : ''}
    ${point.estimatedArrival ? `<time>${new Date(point.estimatedArrival).toISOString()}</time>` : ''}
    <sym>Flag, Blue</sym>
    <type>Delivery</type>
    <extensions>
      <order>${index + 1}</order>
      ${point.priority ? `<priority>${point.priority}</priority>` : ''}
      ${point.serviceTime ? `<serviceTime>${point.serviceTime}</serviceTime>` : ''}
      ${timeWindow ? `<timeWindow>${escapeXml(timeWindow)}</timeWindow>` : ''}
    </extensions>
  </wpt>`;
};

/**
 * Cria uma rota GPX a partir dos steps
 * @param {Object} route - Rota otimizada
 * @param {Object} vehicle - Veiculo
 * @param {number} index - Indice da rota
 * @returns {string}
 */
const createRoute = (route, vehicle, index) => {
  const routeName = vehicle?.name || `Rota ${index + 1}`;
  const steps = route.steps || [];

  let routePoints = '';

  steps.forEach((step, stepIndex) => {
    const lat = step.latitude || step.lat || step.location?.[1];
    const lon = step.longitude || step.lng || step.location?.[0];

    if (!lat || !lon) return;

    routePoints += `
    <rtept lat="${lat}" lon="${lon}">
      <name>${escapeXml(step.name || step.description || `Ponto ${stepIndex + 1}`)}</name>
      ${step.address ? `<desc>${escapeXml(step.address)}</desc>` : ''}
      ${step.arrival ? `<time>${new Date(step.arrival * 1000).toISOString()}</time>` : ''}
      <sym>${step.type === 'start' ? 'Flag, Green' : step.type === 'end' ? 'Flag, Red' : 'Flag, Blue'}</sym>
      <type>${getStopTypeName(step.type)}</type>
    </rtept>`;
  });

  return `
  <rte>
    <name>${escapeXml(routeName)}</name>
    <desc>Distancia: ${formatKm(route.distance)} | Tempo: ${formatTime(route.duration)}</desc>
    <number>${index + 1}</number>${routePoints}
  </rte>`;
};

/**
 * Cria um track GPX a partir da geometria da rota
 * @param {Object} route - Rota com geometria
 * @param {Object} vehicle - Veiculo
 * @param {number} index - Indice da rota
 * @returns {string}
 */
const createTrack = (route, vehicle, index) => {
  const trackName = vehicle?.name || `Trajeto ${index + 1}`;

  // Se tiver geometria do percurso (polyline decodificada ou array de coordenadas)
  let geometry = route.geometry || route.routeGeometry;
  if (!geometry) return '';

  // Se for uma polyline encoded, precisamos decodificar
  let coordinates = [];
  if (typeof geometry === 'string') {
    coordinates = decodePolyline(geometry);
  } else if (Array.isArray(geometry)) {
    coordinates = geometry;
  }

  if (coordinates.length === 0) return '';

  let trackPoints = '';
  coordinates.forEach(([lat, lon]) => {
    trackPoints += `
      <trkpt lat="${lat}" lon="${lon}"></trkpt>`;
  });

  return `
  <trk>
    <name>${escapeXml(trackName)}</name>
    <desc>Trajeto completo - ${formatKm(route.distance)}</desc>
    <number>${index + 1}</number>
    <trkseg>${trackPoints}
    </trkseg>
  </trk>`;
};

/**
 * Decodifica uma polyline encoded (formato Google)
 * @param {string} encoded - String encoded
 * @returns {Array<Array<number>>} Array de [lat, lon]
 */
const decodePolyline = (encoded) => {
  if (!encoded) return [];

  const coordinates = [];
  let index = 0;
  let lat = 0;
  let lng = 0;

  while (index < encoded.length) {
    let b;
    let shift = 0;
    let result = 0;

    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);

    const dlat = (result & 1) ? ~(result >> 1) : (result >> 1);
    lat += dlat;

    shift = 0;
    result = 0;

    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);

    const dlng = (result & 1) ? ~(result >> 1) : (result >> 1);
    lng += dlng;

    coordinates.push([lat / 1e5, lng / 1e5]);
  }

  return coordinates;
};

/**
 * Exporta plano de rota para GPX
 * Formato compativel com Garmin, Waze, Google Maps, etc.
 *
 * @param {Object} params - Parametros de exportacao
 * @param {string} params.planName - Nome do plano
 * @param {Array} params.routes - Array de rotas otimizadas
 * @param {Array} params.vehicles - Array de veiculos
 * @param {Array} params.deliveryPoints - Array de pontos de entrega
 * @param {boolean} params.includeWaypoints - Incluir waypoints individuais
 * @param {boolean} params.includeRoutes - Incluir rotas (sequencia de pontos)
 * @param {boolean} params.includeTracks - Incluir tracks (geometria completa)
 */
export const exportRoutePlanGPX = ({
  planName = 'Plano de Rota',
  routes = [],
  vehicles = [],
  deliveryPoints = [],
  includeWaypoints = true,
  includeRoutes = true,
  includeTracks = true,
}) => {
  let gpxContent = gpxHeader(planName);

  // Adicionar waypoints (pontos de entrega individuais)
  if (includeWaypoints && deliveryPoints.length > 0) {
    deliveryPoints.forEach((point, index) => {
      gpxContent += createWaypoint(point, index);
    });
  }

  // Adicionar rotas (sequencia de paradas)
  if (includeRoutes && routes.length > 0) {
    routes.forEach((route, index) => {
      const vehicle = vehicles.find((v) => v.id === route.vehicleId);
      gpxContent += createRoute(route, vehicle, index);
    });
  }

  // Adicionar tracks (geometria completa do trajeto)
  if (includeTracks && routes.length > 0) {
    routes.forEach((route, index) => {
      const vehicle = vehicles.find((v) => v.id === route.vehicleId);
      gpxContent += createTrack(route, vehicle, index);
    });
  }

  gpxContent += `\n${gpxFooter}`;

  // Salvar arquivo
  const blob = new Blob([gpxContent], { type: 'application/gpx+xml' });
  const filename = `${planName.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.gpx`;
  saveAs(blob, filename);

  return gpxContent;
};

/**
 * Exporta uma unica rota para GPX
 * Ideal para enviar ao motorista
 *
 * @param {Object} params - Parametros de exportacao
 * @param {Object} params.route - Rota otimizada
 * @param {Object} params.vehicle - Veiculo
 * @param {string} params.routeName - Nome da rota
 */
export const exportSingleRouteGPX = ({
  route,
  vehicle,
  routeName = null,
}) => {
  const name = routeName || vehicle?.name || 'Rota';

  let gpxContent = gpxHeader(name);

  // Waypoints para cada parada
  const steps = route.steps || [];
  steps.forEach((step, index) => {
    const lat = step.latitude || step.lat || step.location?.[1];
    const lon = step.longitude || step.lng || step.location?.[0];

    if (!lat || !lon) return;

    gpxContent += `
  <wpt lat="${lat}" lon="${lon}">
    <name>${escapeXml(`${index + 1}. ${step.name || step.description || getStopTypeName(step.type)}`)}</name>
    ${step.address ? `<desc>${escapeXml(step.address)}</desc>` : ''}
    ${step.arrival ? `<time>${new Date(step.arrival * 1000).toISOString()}</time>` : ''}
    <sym>${step.type === 'start' ? 'Flag, Green' : step.type === 'end' ? 'Flag, Red' : 'Circle, Blue'}</sym>
  </wpt>`;
  });

  // Rota com sequencia de pontos
  gpxContent += createRoute(route, vehicle, 0);

  // Track com geometria
  gpxContent += createTrack(route, vehicle, 0);

  gpxContent += `\n${gpxFooter}`;

  // Salvar arquivo
  const blob = new Blob([gpxContent], { type: 'application/gpx+xml' });
  const filename = `${name.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.gpx`;
  saveAs(blob, filename);

  return gpxContent;
};

/**
 * Gera link para abrir rota no Google Maps
 * @param {Object} route - Rota otimizada
 * @returns {string} URL do Google Maps
 */
export const generateGoogleMapsUrl = (route) => {
  const steps = route.steps || [];
  if (steps.length < 2) return null;

  const origin = steps[0];
  const destination = steps[steps.length - 1];
  const waypoints = steps.slice(1, -1);

  let url = 'https://www.google.com/maps/dir/?api=1';

  // Origem
  const originLat = origin.latitude || origin.lat || origin.location?.[1];
  const originLon = origin.longitude || origin.lng || origin.location?.[0];
  if (originLat && originLon) {
    url += `&origin=${originLat},${originLon}`;
  }

  // Destino
  const destLat = destination.latitude || destination.lat || destination.location?.[1];
  const destLon = destination.longitude || destination.lng || destination.location?.[0];
  if (destLat && destLon) {
    url += `&destination=${destLat},${destLon}`;
  }

  // Waypoints (maximo 10 para URL)
  if (waypoints.length > 0) {
    const waypointCoords = waypoints
      .slice(0, 10)
      .map((wp) => {
        const lat = wp.latitude || wp.lat || wp.location?.[1];
        const lon = wp.longitude || wp.lng || wp.location?.[0];
        return lat && lon ? `${lat},${lon}` : null;
      })
      .filter(Boolean);

    if (waypointCoords.length > 0) {
      url += `&waypoints=${waypointCoords.join('|')}`;
    }
  }

  url += '&travelmode=driving';

  return url;
};

/**
 * Gera link para abrir rota no Waze
 * @param {Object} route - Rota otimizada
 * @returns {string} URL do Waze
 */
export const generateWazeUrl = (route) => {
  const steps = route.steps || [];
  if (steps.length < 1) return null;

  // Waze abre para o proximo destino
  const destination = steps.find((s) => s.type === 'job') || steps[1] || steps[0];

  const lat = destination.latitude || destination.lat || destination.location?.[1];
  const lon = destination.longitude || destination.lng || destination.location?.[0];

  if (!lat || !lon) return null;

  return `https://waze.com/ul?ll=${lat},${lon}&navigate=yes`;
};

export default {
  exportRoutePlanExcel,
  exportRoutePlanPdf,
  exportRoutePlanGPX,
  exportSingleRouteGPX,
  generateGoogleMapsUrl,
  generateWazeUrl,
};
